import { Link } from "react-router-dom";
import { ArrowRight, Award, ShieldCheck, FileText, ChevronRight } from "lucide-react";
import SEO, { orgJsonLd, breadcrumbJsonLd } from "../components/SEO";
import Reveal, { Eyebrow } from "../components/Reveal";
import LeadForm from "../components/LeadForm";
import StatsBar from "../components/StatsBar";
import Testimonials from "../components/Testimonials";

const METHODS = [
  { name: "Discounted Cash Flow (DCF)", desc: "Intrinsic value from projected free cash flows, discounted at a defensible rate. The core method for growth-stage companies where the future matters more than the past.", tag: "Most used for startups" },
  { name: "Net Asset Value (NAV)", desc: "Asset-based valuation for holding companies, manufacturing businesses and asset-heavy structures. Adjusted book value with fair-value corrections.", tag: "Asset-heavy businesses" },
  { name: "Comparable Company Analysis", desc: "Market multiples from listed peers and recent private transactions in your sector. The method investors instinctively sanity-check first.", tag: "Market reality check" },
  { name: "Income Approach", desc: "Capitalisation of maintainable earnings for profitable SMEs — the method banks and conservative family offices trust most.", tag: "Profitable SMEs" },
];

const STEPS = [
  { num: "01", title: "Discovery", desc: "Deep-dive on your model, market, unit economics and the story behind the numbers. One structured session, one week.", dur: "Week 1" },
  { num: "02", title: "Data Room", desc: "We assemble audited financials, cap table, contracts and compliance certificates into an investor-grade data room.", dur: "Weeks 2–3" },
  { num: "03", title: "Valuation Report", desc: "IBBI-registered valuer signs a report triangulating DCF, comparables and NAV — defensible under Companies Act and FEMA.", dur: "Weeks 3–4" },
  { num: "04", title: "Investor Pitch", desc: "Deck, financial model and rehearsed narrative. Warm introductions to angels, VCs and family offices from our network.", dur: "Weeks 5–8" },
  { num: "05", title: "Term Sheet", desc: "We model every clause against your 5-year outcome — liquidation preference, anti-dilution, board rights — before you sign.", dur: "Weeks 9–12" },
];

export default function ValuationPage() {
  return (
    <>
      <SEO
        title="Raising Funds? Startup Valuation & Fundraising Advisory India | Complisure Counsultancy"
        description="IBBI-registered valuer reports, DCF & comparable valuation, pitch decks and investor connect — we raise funds for early-stage and growth-stage companies. 550+ Cr valued, 800+ clients."
        canonical="https://compliance-capital-1.preview.emergentagent.com/valuation-fundraising"
        jsonLd={[
          orgJsonLd,
          breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Valuation & Fundraising", path: "/valuation-fundraising" }]),
          {
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            name: "Complisure Counsultancy — Valuation & Fundraising Advisory",
            telephone: "+91-73510-10107",
            areaServed: "IN",
            description: "Registered valuer reports, fundraising advisory and investor connect for early-stage and growth-stage companies raising funds in India.",
          },
        ]}
      />

      <section className="relative overflow-hidden pb-20 pt-36">
        <div className="blob blob-float left-[-8%] top-[-5%] h-[30rem] w-[30rem] bg-brand-blue/12" />
        <div className="blob blob-float-slow right-[-10%] top-[30%] h-[26rem] w-[26rem] bg-brand-sky/15" />
        <div className="dot-grid absolute inset-0 opacity-[0.1]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <Reveal>
              <Eyebrow>Valuation & Fundraising</Eyebrow>
              <h1 className="mt-4 font-display text-4xl font-extrabold leading-[1.08] tracking-[-0.02em] text-brand-navy sm:text-5xl lg:text-[3.4rem]">
                <span className="mask-line"><span>Raising Funds?</span></span>
                <span className="mask-line"><span style={{ animationDelay: "0.12s" }}>Your valuation is</span></span>
                <span className="mask-line"><span style={{ animationDelay: "0.24s" }} className="text-gradient">the opening argument.</span></span>
              </h1>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-brand-gray sm:text-lg">
                Investors don't reject numbers — they reject undefended ones. We raise funds for <strong className="text-brand-navy">early-stage and growth-stage companies</strong>: registered-valuer reports, investor-grade decks and warm introductions — with <strong className="text-brand-navy">₹550+ Cr in valuations</strong> supported across 800+ clients, from seed decks to Series B term sheets.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <a href="#valuation-form" data-testid="valuation-hero-cta" className="btn-gradient inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-brand-blue/30">
                  Get a Valuation Report <ArrowRight className="h-4 w-4" />
                </a>
                <Link to="/case-studies" data-testid="valuation-hero-cases" className="inline-flex items-center gap-2 rounded-full border border-brand-blue/25 bg-white/60 px-7 py-3.5 text-sm font-semibold text-brand-blue backdrop-blur transition-all hover:-translate-y-0.5 hover:bg-brand-ice">
                  See Funding Outcomes
                </Link>
              </div>
              <div className="mt-10 flex flex-wrap gap-3">
                {[
                  { icon: Award, label: "IBBI Registered Valuers" },
                  { icon: ShieldCheck, label: "Merchant Banker Network" },
                  { icon: FileText, label: "Companies Act & FEMA Compliant" },
                ].map((b) => (
                  <span key={b.label} data-testid={`badge-${b.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="glass inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-brand-navy">
                    <b.icon className="h-4 w-4 text-brand-blue" /> {b.label}
                  </span>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="glass card-lift overflow-hidden rounded-[28px] p-2.5">
                <img
                  src="https://images.unsplash.com/photo-1716703742352-0bbdb45f505b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBmaW5hbmNpYWwlMjBhZHZpc29yJTIwY29ycG9yYXRlJTIwYnVpbGRpbmclMjBzdGFydHVwJTIwZm91bmRlcnN8ZW58MHx8fHwxNzg4Nzg2MTEwfDA&ixlib=rb-4.1.0&q=85"
                  alt="Founders in a fundraising strategy meeting"
                  className="h-96 w-full rounded-[20px] object-cover"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <StatsBar />

      <section className="bg-brand-ice/50 py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal className="mb-12 max-w-3xl">
            <Eyebrow>Valuation Methods</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-brand-navy sm:text-4xl">Four lenses, one defensible number</h2>
            <p className="mt-4 text-base leading-relaxed text-brand-gray sm:text-lg">
              Serious reports triangulate. When methods converge inside a defensible band, negotiation shifts from the number to the terms — where rounds are actually won.
            </p>
          </Reveal>
          <div className="grid gap-5 sm:grid-cols-2">
            {METHODS.map((m, i) => (
              <Reveal key={m.name} delay={(i % 2) * 0.1}>
                <div data-testid={`method-${m.name.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 30)}`} className="glass card-lift h-full rounded-3xl p-8">
                  <span className="rounded-full bg-brand-ice px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-brand-blue">{m.tag}</span>
                  <h3 className="mt-4 font-display text-xl font-bold tracking-tight text-brand-navy">{m.name}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-brand-gray">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-24">
        <div className="dot-grid absolute inset-0 opacity-[0.08]" />
        <div className="relative mx-auto max-w-5xl px-4 sm:px-6">
          <Reveal className="mb-14 max-w-3xl">
            <Eyebrow>The Process</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-brand-navy sm:text-4xl">Discovery to term sheet in 90 days</h2>
          </Reveal>
          <div className="relative">
            <div className="absolute left-6 top-0 h-full w-px bg-gradient-to-b from-brand-blue via-brand-sky to-transparent sm:left-1/2" />
            <div className="space-y-10">
              {STEPS.map((s, i) => (
                <Reveal key={s.num} delay={0.05}>
                  <div data-testid={`process-step-${s.num}`} className={`relative flex flex-col gap-4 sm:flex-row sm:items-center ${i % 2 ? "sm:flex-row-reverse" : ""}`}>
                    <div className={`sm:w-1/2 ${i % 2 ? "sm:pl-14" : "sm:pr-14 sm:text-right"} pl-16 sm:pl-16 ${i % 2 ? "sm:pl-14" : ""}`}>
                      <div className="glass card-lift rounded-3xl p-6">
                        <span className="text-xs font-bold uppercase tracking-[0.18em] text-brand-blue">{s.dur}</span>
                        <h3 className="mt-1.5 font-display text-lg font-bold text-brand-navy">{s.title}</h3>
                        <p className="mt-2 text-sm leading-relaxed text-brand-gray">{s.desc}</p>
                      </div>
                    </div>
                    <div className="absolute left-6 flex h-11 w-11 -translate-x-1/2 items-center justify-center rounded-full bg-gradient-to-br from-brand-blue to-brand-sky font-display text-sm font-extrabold text-white shadow-lg shadow-brand-blue/30 sm:left-1/2">
                      {s.num}
                    </div>
                    <div className="hidden sm:block sm:w-1/2" />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Testimonials title="Founders who raised with us in the room" />

      <section id="valuation-form" className="relative bg-brand-navy py-24 text-white">
        <div className="blob blob-float left-[5%] top-[-10%] h-96 w-96 bg-brand-blue/25" />
        <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>Get Started</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">Get your valuation report</h2>
            <p className="mt-4 text-base leading-relaxed text-white/60 sm:text-lg">
              Tell us about your company and target round. A registered valuer will call within one working day with scope, timeline and a fixed quote.
            </p>
            <ul className="mt-6 space-y-3 text-sm text-white/70">
              {["Signed by IBBI-registered valuers", "Valid for Companies Act, FEMA & income tax purposes", "Fixed fee, quoted before we start"].map((t) => (
                <li key={t} className="flex items-center gap-3">
                  <ChevronRight className="h-4 w-4 text-brand-sky" /> {t}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.12}>
            <LeadForm defaultService="Business Valuation Report" sourcePage="/valuation-fundraising" />
          </Reveal>
        </div>
      </section>
    </>
  );
}
